import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardRefundOrder from '@/domains/mobile-web/components/CardRefundOrder/CardRefundOrder';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
  type RefundStatusType,
  STATUS_REFUND,
} from '@/domains/mobile-web/constant/listStatusRefund.constant';
import { REFUND_LIST, type RefundListItem } from '@/domains/mobile-web/constant/refund.constant';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import StatusFilter from '@/shared/components/ui/StatusFilter/StatusFilter';

type FilterId = 'all' | RefundStatusType;

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'Semua' },
  ...(Object.entries(STATUS_REFUND) as [RefundStatusType, { label: string }][]).map(
    ([id, item]) => ({ id, label: item.label })
  ),
];

/** Daftar pengajuan refund (statis). Tombol aksi tiap kartu ditentukan oleh CardRefundOrder. */
const RefundList = () => {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<FilterId>('all');
  const [keyword, setKeyword] = useState('');

  const items = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return REFUND_LIST.filter((item) => {
      if (selectedStatus !== 'all' && item.status !== selectedStatus) return false;
      if (!q) return true;
      const inCode = item.code.toLowerCase().includes(q);
      const inTitle = item.products.some((p) => p.product.title.toLowerCase().includes(q));
      return inCode || inTitle;
    });
  }, [selectedStatus, keyword]);

  // Halaman tujuan Details per status (sementara, sampai detail per id tersedia)
  const openDetail = (item: RefundListItem) =>
    navigate(item.status === 'rejected' ? '/store/refund-rejected' : '/store/refund?status=review');

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" onBack={() => navigate(-1)} title="Refund & Pengembalian" />
      </HeaderMobile>

      <WrapperMobile>
        <input
          type="text"
          className="input-db-text"
          placeholder="Cari nomor invoice, judul buku..."
          aria-label="Cari nomor invoice atau judul buku"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />

        <div className="overflow-x-auto gap-3 inline-flex">
          <StatusFilter
            items={FILTERS}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value as FilterId)}
          />
        </div>

        {items.length === 0 ? (
          <p className="body1 text-text-grey text-center py-6">Belum ada pengajuan refund.</p>
        ) : (
          items.map((item) => (
            <CardRefundOrder
              key={item.id}
              variant="full"
              date={item.date}
              status={item.status}
              code={item.code}
              products={item.products}
              shipping={item.shipping}
              estimatedRefund={item.estimatedRefund}
              onDetail={() => openDetail(item)}
              onStartReturn={() => navigate('/store/refund')}
              onTrack={() => navigate('/store/track')}
            />
          ))
        )}
      </WrapperMobile>
    </main>
  );
};

export default RefundList;
